import React, { useState, useEffect, useCallback } from "react";
import { Modal, Switch, Tag, Spin, Divider } from "antd";
import type { Job } from "../../../../core/data/redux/recruitment/types";

interface PlatformState {
  value: string;
  label: string;
  icon: string;
  description: string;
  isPublished: boolean;
  toggled: boolean;
}

interface PublishModalProps {
  open: boolean;
  job: Job | null;
  loading: boolean;
  onCancel: () => void;
  onPublish: (jobId: number, platforms: string[]) => Promise<void>;
  onUnpublish: (jobId: number, platform: string) => Promise<void>;
}

const PLATFORM_CONFIG = [
  {
    value: "career_page",
    label: "Career Page",
    icon: "ti-world",
    description: "Your company's public career page",
  },
  {
    value: "linkedin",
    label: "LinkedIn",
    icon: "ti-brand-linkedin",
    description: "Reach professionals on LinkedIn",
  },
  {
    value: "indeed",
    label: "Indeed",
    icon: "ti-briefcase",
    description: "Post to the world's #1 job site",
  },
  {
    value: "naukri",
    label: "Naukri",
    icon: "ti-building",
    description: "India's largest job portal",
  },
];

const PublishModal: React.FC<PublishModalProps> = ({
  open,
  job,
  loading,
  onCancel,
  onPublish,
  onUnpublish,
}) => {
  const [platforms, setPlatforms] = useState<PlatformState[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    const publishedSet = new Set(
      (job?.platforms || []).map((p: any) => p.platform),
    );

    setPlatforms(
      PLATFORM_CONFIG.map((config) => ({
        ...config,
        isPublished: publishedSet.has(config.value),
        toggled: publishedSet.has(config.value),
      })),
    );
  }, [open, job]);

  const handleToggle = useCallback((platformValue: string, checked: boolean) => {
    setPlatforms((prev) =>
      prev.map((p) =>
        p.value === platformValue ? { ...p, toggled: checked } : p,
      ),
    );
  }, []);

  const toPublish = platforms.filter((p) => !p.isPublished && p.toggled);
  const toUnpublish = platforms.filter(
    (p) => p.isPublished && !p.toggled
  );
  const hasChanges = toPublish.length > 0 || toUnpublish.length > 0;

  const handleSave = async () => {
    if (!job || !hasChanges) return;
    setSubmitting(true);
    try {
      if (toPublish.length > 0) {
        await onPublish(job.id, toPublish.map((p) => p.value));
      }
      for (const platform of toUnpublish) {
        await onUnpublish(job.id, platform.value);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isLoading = loading || submitting;

  return (
    <Modal
      title={
        <span className="d-flex align-items-center gap-2">
          <i className="ti ti-share fs-4 text-primary" />
          Manage Publishing
        </span>
      }
      open={open}
      onCancel={!isLoading ? onCancel : undefined}
      onOk={handleSave}
      okText={
        isLoading
          ? "Saving..."
          : hasChanges
            ? `Apply Changes (${toPublish.length + toUnpublish.length})`
            : "No Changes"
      }
      confirmLoading={isLoading}
      okButtonProps={{ disabled: !hasChanges || isLoading }}
      cancelButtonProps={{ disabled: isLoading }}
      centered
      width={500}
      keyboard={false}
      maskClosable={!isLoading}
      zIndex={1001}
      styles={{
        mask: {
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(0,0,0,0.45)",
        },
      }}
    >
      <Spin spinning={isLoading}>
        <div className="py-2">
          
          {job && (
            <>
              <div className="mb-3">
                <h6 className="fw-semibold mb-0 text-truncate">{job.job_title}</h6>
                <p className="text-muted small mb-0">
                  Toggle platforms to publish or unpublish this job
                </p>
              </div>
              <Divider className="my-3" />
            </>
          )}

          
          <div className="d-flex flex-column gap-3">
            {platforms.map((platform) => {
              const willChange = platform.toggled !== platform.isPublished;
              const willPublish = !platform.isPublished && platform.toggled;
              const willUnpublish = platform.isPublished && !platform.toggled;

              return (
                <div
                  key={platform.value}
                  className="d-flex align-items-center justify-content-between p-3 rounded"
                  style={{
                    border: "1px solid",
                    borderColor: willChange ? "#4f46e5" : "#e2e8f0",
                    background: willChange ? "#f5f3ff" : "#fafafa",
                    transition: "all 0.2s",
                  }}
                >
                  
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: 36,
                        height: 36,
                        background: platform.toggled ? "#ede9fe" : "#f1f5f9",
                      }}
                    >
                      <i
                        className={`ti ${platform.icon} fs-20`}
                        style={{ color: platform.toggled ? "#7c3aed" : "#94a3b8" }}
                      />
                    </div>

                    <div>
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-medium small">{platform.label}</span>

                        
                        {platform.isPublished && !willChange && (
                          <Tag color="green" style={{ margin: 0, fontSize: 10 }}>
                            Live
                          </Tag>
                        )}
                        {willPublish && (
                          <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>
                            Will publish
                          </Tag>
                        )}
                        {willUnpublish && (
                          <Tag color="orange" style={{ margin: 0, fontSize: 10 }}>
                            Will unpublish
                          </Tag>
                        )}

                      </div>
                      <span className="text-muted" style={{ fontSize: 11 }}>
                        {platform.description}
                      </span>
                    </div>
                  </div>

                  
                  <Switch
                    checked={platform.toggled}
                    disabled={isLoading}
                    onChange={(checked) => handleToggle(platform.value, checked)}
                    size="small"
                  />
                </div>
              );
            })}
          </div>

          
          {hasChanges && (
            <div
              className="mt-3 p-2 rounded small"
              style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}
            >
              <i className="ti ti-info-circle me-1 text-primary" />
              {[
                toPublish.length > 0 &&
                `Publishing to: ${toPublish.map((p) => p.label).join(", ")}`,
                toUnpublish.length > 0 &&
                `Unpublishing from: ${toUnpublish.map((p) => p.label).join(", ")}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default PublishModal;